// Pass-or-fail for every test, after every run.
//
// A run with tests on must end with one result per test_list row - never "not run" - even
// when the student's code has a syntax error, one test raises, the process crashes or the
// runner fails. Two layers make that hold:
//   1. The generated harnesses give every test its own try/except (Python) or try/catch
//      (C++), so one raising test is marked failed and the rest still run.
//      upgradeLegacyPythonHarness brings harnesses saved before this to the same shape at
//      run time, without recompiling.
//   2. completeTestResults pads whatever a run produced to the full list, missing = failed.
//      That covers what no harness can catch: syntax errors, segfaults, timeouts.
// Before the first run nothing has run yet, so rows stay pending until then.

import { isHiddenTest } from './testVisibility'

// One boolean per test: true only for an explicit pass. Pads to the test count but never
// cuts results off: a harness not built from test_list (Java Classes, a hand-written one)
// can report more results than the list has rows, and those still count.
export const completeTestResults = (raw, testCount) => {
  const list = Array.isArray(raw) ? raw : []
  const wanted = Number.isInteger(testCount) && testCount > 0 ? testCount : 0
  return Array.from({ length: Math.max(wanted, list.length) }, (_, index) => list[index] === true)
}

// The C++ harness writes one result per test to stderr, as "VIZPI:1" / "VIZPI:0" on a line
// of its own (buildCppTestLine starts it with a newline, so a student's own cerr text left
// without one cannot run into it and shift every later result). Harnesses from before the
// marker write bare "1" / "0" lines, and one an instructor edited by hand can mix the two,
// so both are read, in order. Anything else there - the runtime's "terminate called..."
// text, student output - is not a result.
const CPP_RESULT_LINES = { 'VIZPI:1': true, 'VIZPI:0': false, 1: true, 0: false }
export const parseCppHarness = (stderr) =>
  String(stderr || '')
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => Object.prototype.hasOwnProperty.call(CPP_RESULT_LINES, line))
    .map((line) => CPP_RESULT_LINES[line])

// The Java harness prints one "true" or "false" line per test to stderr; stack traces and
// javac output are not results.
export const parseJavaHarness = (stderr) =>
  String(stderr || '')
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter((line) => line === 'true' || line === 'false')
    .map((line) => line === 'true')

// Set by the first line of every harness a runner executes (upgradeLegacyPythonHarness).
const HARNESS_STARTED = '__vizpi_harness_started'

// Reads the harness's global `result` list from Pyodide globals (or a per-run dict). [] when
// the harness never started: a run that failed in the student's own code before it would
// otherwise be scored on whatever `result` that code left behind - a list of its own, even
// one set to all True on purpose - and saved as passes.
export const readPythonResult = (globals) => {
  try {
    if (globals.get(HARNESS_STARTED) !== true) return []
    const proxy = globals.get('result')
    if (proxy && typeof proxy.toJs === 'function') {
      const value = proxy.toJs()
      proxy.destroy?.()
      return Array.isArray(value) ? value : []
    }
    return Array.isArray(proxy) ? proxy : []
  } catch (error) {
    return []
  }
}

// What a failing test prints. Only the exception's type: its message can quote a hidden
// test's input (e.g. "invalid literal for int(): 'abc'").
// The harness's line when a hidden test's call printed something, which stays hidden (it could
// show the test's inputs). Study pages add a note on how to see one's own output (printNote.js).
export const HIDDEN_OUTPUT_NOTICE = '(Output printed while the hidden tests ran is not shown.)'

const PY_FAILED_NOTE = 'A test raised " + vizpi_error.__class__.__name__ + " and was marked as failed.'

// The Python harness (runs after the starter, which sets parameter_values, return_values
// and result). `result = []` again here so a student's own top-level `result` cannot break
// it. The comparison is inside the try too, so an ambiguous == (numpy) fails only its test.
// BaseException also catches exit() inside the student's function. Built-ins are reached
// through the builtins module: a beginner's `type = "circle"` or `print = ...` must not
// break the harness itself.
export const buildPythonHarness = (functionName) => `import builtins as vizpi_builtins
result = []
for i, input_values in vizpi_builtins.enumerate(parameter_values):
  try:
    vizpi_passed = vizpi_builtins.bool(${functionName}(*input_values) == return_values[i])
  except vizpi_builtins.BaseException as vizpi_error:
    vizpi_passed = False
    vizpi_builtins.print("${PY_FAILED_NOTE}")
  result.append(vizpi_passed)`

// The loop createTestCode generated before per-test isolation, exactly. Harnesses an
// instructor edited by hand do not match and are left alone.
const LEGACY_PYTHON_HARNESS =
  /^\s*for i, input_values in enumerate\(parameter_values\):\r?\n {2}if (.+?)\(\*input_values\) == return_values\[i\]:\r?\n {4}result\.append\(True\)\r?\n {2}else:\r?\n {4}result\.append\(False\)\s*$/

// Call just before building the source to run, for every harness. Two jobs:
//   - saved harnesses only change when the tests are recompiled, so the exact old generated
//     loop is swapped for the per-test-isolated one here;
//   - every harness gets a first line marking that it started, which readPythonResult
//     requires (so a run that failed before the harness reads no results at all).
// An empty harness stays empty: there is nothing to run, and no result to read.
export const upgradeLegacyPythonHarness = (unitTest) => {
  if (typeof unitTest !== 'string' || !unitTest.trim()) return unitTest
  const match = unitTest.match(LEGACY_PYTHON_HARNESS)
  return `${HARNESS_STARTED} = True\n${match ? buildPythonHarness(match[1]) : unitTest}`
}

// ---- keeping hidden tests hidden on a student's run ----
//
// Python runs in the student's browser, in one namespace with the Variable Setup and the
// harness. Run as saved, that gave a student's code two ways into the hidden tests: the
// setup's `parameter_values` / `return_values` globals (every input and expected output -
// enough to pass without solving, by looking the answer up), and print() inside the tested
// function, which showed each hidden input in the console.
//
// For the generated setup and harness, a student's run uses this pair instead:
//   - the prelude runs the setup in a private namespace and passes on only what is not test
//     data (helpers an instructor may have added), so the data never becomes a global;
//   - the harness reads the data inside a function of its own, and while a hidden test runs
//     sends everything the student's code prints (stdout, stderr, sys.__stdout__) to a
//     buffer that is thrown away. Public tests print as before.
// Results come out exactly as from buildPythonHarness: `result` plus the started marker.
//
// This stops the easy routes, not a determined student: the page itself still carries the
// hidden tests (session data, devtools), because the browser has to run them. Only grading
// on the server keeps them secret. A harness or setup an instructor wrote by hand does not
// match and runs as saved.

const SETUP_TEST_DATA = ['parameter_values', 'return_values']

// The tested function's name when `upgraded` (an upgradeLegacyPythonHarness result) is the
// generated harness, unchanged; otherwise null.
const generatedHarnessFunction = (upgraded) => {
  const normalized = String(upgraded || '').replace(/\r\n/g, '\n').trimEnd()
  const match = normalized.match(/vizpi_builtins\.bool\(([A-Za-z_][A-Za-z0-9_]*)\(\*input_values\) == return_values\[i\]\)/)
  if (!match) return null
  return normalized === `${HARNESS_STARTED} = True\n${buildPythonHarness(match[1])}` ? match[1] : null
}

// A JS string as a Python string literal (JSON's escapes are all valid in Python).
const pyLiteral = (text) => JSON.stringify(String(text))

// testData: the setup's names that hold test data (never passed on).
const buildSafePrelude = (setup, testData = SETUP_TEST_DATA) => `def __vizpi_load_setup():
  vizpi_setup = {}
  exec(${pyLiteral(setup)}, vizpi_setup)
  for vizpi_name, vizpi_value in list(vizpi_setup.items()):
    if vizpi_name not in (${testData.map((name) => `'${name}'`).join(', ')}, 'result', '__builtins__'):
      globals()[vizpi_name] = vizpi_value
__vizpi_load_setup()
del __vizpi_load_setup`

// ---- an exercise's provided code ----
//
// A class exercise may come with code the student is given and does not write: test_code
// .provided = { code, version } (E3's Person class). The page shows it read-only under the task
// (ProvidedCode.jsx), never in the editor, and every run and every test loads it before the
// student's code: it joins the end of the setup (withProvidedCode), which runs before the
// student's code in the run itself (buildSafePrelude) and again in each check's fresh namespace
// (PY_FRESH_GLOBALS). Compiled as "<provided>", it never shifts the student's line numbers, and
// an error inside it names its own lines, the ones the page shows.

// test_code (an object, or the JSON string older rows store) -> { code, version }, or null.
export const providedCodeOf = (testCode) => {
  let parsed = testCode
  if (typeof testCode === 'string') {
    try {
      parsed = JSON.parse(testCode)
    } catch (error) {
      parsed = null
    }
  }
  const provided = parsed && typeof parsed === 'object' ? parsed.provided : null
  if (!provided || typeof provided.code !== 'string' || !provided.code.trim()) return null
  return { code: provided.code, version: typeof provided.version === 'string' && provided.version ? provided.version : null }
}

// The setup with `provided` (providedCodeOf's) loaded at its end, as one line; a setup of ''
// (tests off) becomes that line alone. Without provided code, the setup as it is. The line first
// gives a namespace without a __name__ (buildSafePrelude's own) one, so the provided classes belong
// to __main__ in every run, as the student's do. The check harness runs the setup once more to
// read its checks, so provided code should only define things: anything it prints at load shows.
export const withProvidedCode = (setup, provided) => {
  if (!provided) return setup
  const loader = `globals().setdefault("__name__", "__main__"); exec(compile(${pyLiteral(provided.code)}, "<provided>", "exec"))`
  return setup ? `${setup}\n${loader}` : loader
}

// Runs after the student's code, so built-ins are reached through the builtins module (a
// student's `print = ...` or `list = ...` must not break it), as in buildPythonHarness.
const buildSafeHarness = (setup, functionName, hiddenFlags) => `import builtins as vizpi_builtins
${HARNESS_STARTED} = True
result = []
def __vizpi_run_tests():
  import sys as vizpi_sys, io as vizpi_io
  vizpi_data = {}
  vizpi_builtins.exec(${pyLiteral(setup)}, vizpi_data)
  vizpi_inputs = vizpi_data['parameter_values']
  vizpi_expected = vizpi_data['return_values']
  vizpi_hidden = [${hiddenFlags.map((hidden) => (hidden ? 'True' : 'False')).join(', ')}]
  vizpi_main = vizpi_builtins.globals()
  vizpi_results = []
  vizpi_held_back = False
  for vizpi_i, vizpi_args in vizpi_builtins.enumerate(vizpi_inputs):
    vizpi_is_hidden = vizpi_hidden[vizpi_i] if vizpi_i < vizpi_builtins.len(vizpi_hidden) else True
    vizpi_sink = vizpi_io.StringIO()
    vizpi_streams = (vizpi_sys.stdout, vizpi_sys.stderr, vizpi_sys.__stdout__, vizpi_sys.__stderr__)
    try:
      if vizpi_is_hidden:
        vizpi_sys.stdout = vizpi_sys.stderr = vizpi_sys.__stdout__ = vizpi_sys.__stderr__ = vizpi_sink
      try:
        vizpi_function = vizpi_main['${functionName}'] if '${functionName}' in vizpi_main else vizpi_builtins.__dict__.get('${functionName}')
        if vizpi_function is None:
          raise vizpi_builtins.NameError("name '${functionName}' is not defined")
        vizpi_passed = vizpi_builtins.bool(vizpi_function(*vizpi_args) == vizpi_expected[vizpi_i])
      finally:
        vizpi_sys.stdout, vizpi_sys.stderr, vizpi_sys.__stdout__, vizpi_sys.__stderr__ = vizpi_streams
    except vizpi_builtins.BaseException as vizpi_error:
      vizpi_passed = False
      vizpi_builtins.print("${PY_FAILED_NOTE}")
    if vizpi_is_hidden and vizpi_sink.getvalue():
      vizpi_held_back = True
    vizpi_results.append(vizpi_passed)
  if vizpi_held_back:
    vizpi_builtins.print("${HIDDEN_OUTPUT_NOTICE}")
  return vizpi_results
result = __vizpi_run_tests()
del __vizpi_run_tests`

// ---- Python class exercises (session.test_code.test_style === 'class') ----
//
// A class exercise's tests are checks - test_list rows { testName, check, hidden } - where
// `check` is a few lines of Python that use the student's class and end with one expression
// that must be true. The setup holds them as `checks`, and the harness runs each one in a
// namespace of its own: a copy of the run's globals, so it sees the student's class while
// no check sees another's objects. Its statements run first, then its last line is evaluated.
//
// A copy of the globals still shares the class itself: a list kept on the class (`tasks = []`
// in its body) or in a default argument (`def __init__(self, tasks=[])`) carries what one
// check added into the next, so one bug also fails checks it has nothing to do with. A study
// session's run (hiddenSafePythonRun's freshStatePerCheck) therefore runs the student's code
// again for every check, into a new namespace, so each check starts from the code alone.

export const isClassStyle = (session) => session?.test_code?.test_style === 'class'

// The setup a class exercise's harness reads: `checks` and `result`, as createTestCode
// writes parameter_values / return_values for function tests.
export const buildCheckSetup = (checks) => `# Variable Setup for Unit Tests
global checks
checks = [${checks.map((check) => pyLiteral(check)).join(', ')}]
global result
result = []`

// Runs one check in `vizpi_namespace` and returns the value of its last line.
const PY_RUN_CHECK = `def __vizpi_run_check(vizpi_source, vizpi_namespace):
  import ast as vizpi_ast
  vizpi_tree = vizpi_ast.parse(vizpi_source)
  if not vizpi_tree.body or not vizpi_builtins.isinstance(vizpi_tree.body[-1], vizpi_ast.Expr):
    raise vizpi_builtins.ValueError("A check must end with an expression.")
  vizpi_body = vizpi_ast.Module(body=vizpi_tree.body[:-1], type_ignores=[])
  vizpi_builtins.exec(vizpi_builtins.compile(vizpi_body, "<check>", "exec"), vizpi_namespace)
  vizpi_last = vizpi_ast.Expression(body=vizpi_tree.body[-1].value)
  return vizpi_builtins.eval(vizpi_builtins.compile(vizpi_last, "<check>", "eval"), vizpi_namespace)`

// For freshStatePerCheck: the student's code run again for one check, in a new __main__
// namespace that first gets what the setup passes on (as buildSafePrelude does). It prints
// nothing, since the run itself already showed the code's output, and reads no input:
// input() raises EOFError instead of asking the student again (code that reads input is not
// run again at all, see readsInput). Whatever it raises fails the check.
const PY_FRESH_GLOBALS = `def __vizpi_fresh_globals(vizpi_setup, vizpi_source):
  import sys as vizpi_sys, io as vizpi_io, types as vizpi_types
  vizpi_namespace = vizpi_types.ModuleType('__main__').__dict__
  vizpi_namespace['__builtins__'] = vizpi_builtins
  vizpi_streams = (vizpi_sys.stdin, vizpi_sys.__stdin__, vizpi_sys.stdout, vizpi_sys.stderr, vizpi_sys.__stdout__, vizpi_sys.__stderr__)
  vizpi_sys.stdin = vizpi_sys.__stdin__ = vizpi_io.StringIO()
  vizpi_sys.stdout = vizpi_sys.stderr = vizpi_sys.__stdout__ = vizpi_sys.__stderr__ = vizpi_io.StringIO()
  try:
    vizpi_builtins.exec(vizpi_setup, vizpi_namespace)
    for vizpi_name in ('checks', 'result'):
      vizpi_namespace.pop(vizpi_name, None)
    vizpi_builtins.exec(vizpi_builtins.compile(vizpi_source, "<exec>", "exec"), vizpi_namespace)
  finally:
    vizpi_sys.stdin, vizpi_sys.__stdin__, vizpi_sys.stdout, vizpi_sys.stderr, vizpi_sys.__stdout__, vizpi_sys.__stderr__ = vizpi_streams
  return vizpi_namespace`

// The class exercise's harness, as saved in test_code.unit_test (the twin of
// buildPythonHarness): every check in its own try/except, a failing one marked failed.
export const buildPythonCheckHarness = () => `import builtins as vizpi_builtins
result = []
${PY_RUN_CHECK}
for vizpi_check in checks:
  try:
    vizpi_passed = vizpi_builtins.bool(__vizpi_run_check(vizpi_check, vizpi_builtins.dict(vizpi_builtins.globals())))
  except vizpi_builtins.BaseException as vizpi_error:
    vizpi_passed = False
    vizpi_builtins.print("${PY_FAILED_NOTE}")
  result.append(vizpi_passed)`

const isGeneratedCheckHarness = (upgraded) =>
  String(upgraded || '').replace(/\r\n/g, '\n').trimEnd() === `${HARNESS_STARTED} = True\n${buildPythonCheckHarness()}`

// The global the check harness leaves next to `result`: one [status, error type, message] per
// check (testOutcomes). A hidden check's message stays empty: it could quote the test.
const PY_DETAILS = '__vizpi_details'

// buildSafeHarness for checks: the checks come from the setup, run in a function of its own,
// and what the student's code prints while a hidden check runs is thrown away. Given
// studentCode (freshStatePerCheck), each check runs on that code run again for it
// (PY_FRESH_GLOBALS) rather than on a copy of the run's globals; without it the harness is
// exactly the one regular sessions have always run, apart from the details below.
//
// Each check also records how it ended (PY_DETAILS): passed, failed (its last line came out
// false), assertion_failed or error. A visible check that raises says so with its number and
// the assert's message or the error ("Test 3 failed: Calling borrow() should mark the book as
// borrowed."); a hidden one keeps the bare note, which names only the exception's type.
const buildSafeCheckHarness = (setup, hiddenFlags, studentCode = null) => {
  const fresh = typeof studentCode === 'string'
  const checkNamespace = fresh
    ? `__vizpi_fresh_globals(${pyLiteral(setup)}, ${pyLiteral(studentCode)})`
    : 'vizpi_builtins.dict(vizpi_main)'
  return `import builtins as vizpi_builtins
${HARNESS_STARTED} = True
result = []
${PY_RUN_CHECK}${fresh ? `\n${PY_FRESH_GLOBALS}` : ''}
def __vizpi_run_tests():
  import sys as vizpi_sys, io as vizpi_io
  vizpi_data = {}
  vizpi_builtins.exec(${pyLiteral(setup)}, vizpi_data)
  vizpi_checks = vizpi_data['checks']
  vizpi_hidden = [${hiddenFlags.map((hidden) => (hidden ? 'True' : 'False')).join(', ')}]
  vizpi_main = vizpi_builtins.globals()
  vizpi_results = []
  vizpi_details = []
  vizpi_held_back = False
  for vizpi_i, vizpi_check in vizpi_builtins.enumerate(vizpi_checks):
    vizpi_is_hidden = vizpi_hidden[vizpi_i] if vizpi_i < vizpi_builtins.len(vizpi_hidden) else True
    vizpi_sink = vizpi_io.StringIO()
    vizpi_streams = (vizpi_sys.stdout, vizpi_sys.stderr, vizpi_sys.__stdout__, vizpi_sys.__stderr__)
    vizpi_status = 'passed'
    vizpi_kind = ''
    vizpi_message = ''
    try:
      if vizpi_is_hidden:
        vizpi_sys.stdout = vizpi_sys.stderr = vizpi_sys.__stdout__ = vizpi_sys.__stderr__ = vizpi_sink
      try:
        vizpi_passed = vizpi_builtins.bool(__vizpi_run_check(vizpi_check, ${checkNamespace}))
      finally:
        vizpi_sys.stdout, vizpi_sys.stderr, vizpi_sys.__stdout__, vizpi_sys.__stderr__ = vizpi_streams
      if not vizpi_passed:
        vizpi_status = 'failed'
    except vizpi_builtins.BaseException as vizpi_error:
      vizpi_passed = False
      vizpi_kind = vizpi_error.__class__.__name__
      vizpi_status = 'assertion_failed' if vizpi_builtins.isinstance(vizpi_error, vizpi_builtins.AssertionError) else 'error'
      if vizpi_is_hidden:
        vizpi_builtins.print("${PY_FAILED_NOTE}")
      else:
        vizpi_message = vizpi_builtins.str(vizpi_error)
        vizpi_label = "Test " + vizpi_builtins.str(vizpi_i + 1)
        if vizpi_status == 'assertion_failed':
          vizpi_builtins.print(vizpi_label + " failed: " + (vizpi_message or "an assert did not hold."))
        else:
          vizpi_builtins.print(vizpi_label + " raised " + vizpi_kind + ((": " + vizpi_message) if vizpi_message else "") + " and was marked as failed.")
    if vizpi_is_hidden and vizpi_sink.getvalue():
      vizpi_held_back = True
    vizpi_results.append(vizpi_passed)
    vizpi_details.append([vizpi_status, vizpi_kind, vizpi_message])
  if vizpi_held_back:
    vizpi_builtins.print("${HIDDEN_OUTPUT_NOTICE}")
  vizpi_main['${PY_DETAILS}'] = vizpi_details
  return vizpi_results
result = __vizpi_run_tests()
del __vizpi_run_tests`
}

// Whether a run's harness started: its first line sets the marker (upgradeLegacyPythonHarness).
export const pythonHarnessStarted = (globals) => {
  try {
    return globals.get(HARNESS_STARTED) === true
  } catch (error) {
    return false
  }
}

// What the check harness recorded (PY_DETAILS): [{ status, errorType, message }] per check,
// or [] when the harness never started or records none (function tests, hand-written ones).
export const readPythonDetails = (globals) => {
  try {
    if (!pythonHarnessStarted(globals)) return []
    const proxy = globals.get(PY_DETAILS)
    const value = proxy && typeof proxy.toJs === 'function' ? proxy.toJs() : proxy
    proxy?.destroy?.()
    return (Array.isArray(value) ? value : []).map((entry) => ({
      status: String(entry?.[0] ?? ''),
      errorType: entry?.[1] ? String(entry[1]) : null,
      message: entry?.[2] ? String(entry[2]) : null,
    }))
  } catch (error) {
    return []
  }
}

const OUTCOMES = ['passed', 'failed', 'assertion_failed', 'error', 'not_run']

// One { status, errorType, message } per test, for the run's records and the Test Cases list.
// status: 'passed'; 'failed' (a check came out false, or a test without details failed);
// 'assertion_failed' and 'error' (the exception's type, and for a visible check its message);
// 'not_run' for every test when the run stopped before the harness - a syntax error, an
// exception at the top level of the student's code, a missing engine - which the results
// alone show only as failures.
export const testOutcomes = (results, details, harnessStarted) =>
  (Array.isArray(results) ? results : []).map((passed, index) => {
    if (!harnessStarted) return { status: 'not_run', errorType: null, message: null }
    const detail = Array.isArray(details) ? details[index] : null
    if (detail && OUTCOMES.includes(detail.status) && detail.status !== 'not_run' && (detail.status === 'passed') === (passed === true)) {
      return { status: detail.status, errorType: detail.errorType || null, message: detail.message || null }
    }
    return { status: passed === true ? 'passed' : 'failed', errorType: null, message: null }
  })

// Whether the student's code may read input: it calls input() or uses stdin. Run again for
// each check it would have nobody to answer it: every check would fail on EOFError, and a
// retry loop around input() that catches the error would never end and freeze the page. Such
// code keeps the shared-copy checks, which reuse the one run the student answered. A call
// written in a comment or string counts too; that only means the checks run as in a regular
// session. The word alone ("# the input task") does not, so comments rarely decide it.
const readsInput = (code) => /\binput\s*\(|\bstdin\b/.test(code)

// For a student's run: { source(userCode), linesBefore } that keeps hidden tests out of reach,
// or null when the setup or harness is not the generated one (run those as saved). Every test
// with tests on is a hidden one unless marked public (testVisibility.isHiddenTest); a test the
// list has no row for counts as hidden. freshStatePerCheck (study sessions): a class
// exercise's checks each run on the student's code run again, not on a shared copy of the
// run's globals; function tests run as they always have. linesBefore: the lines of the source
// before the student's code (studentLineNumbers).
export const hiddenSafePythonRun = ({ starterCode, unitTest, testList, freshStatePerCheck = false }) => {
  if (typeof starterCode !== 'string' || !starterCode.trim()) return null
  // Accept the saved harness or one already through upgradeLegacyPythonHarness.
  const saved = String(unitTest || '').replace(new RegExp(`^${HARNESS_STARTED} = True\\r?\\n`), '')
  const hiddenOf = () => (Array.isArray(testList) ? testList : []).map((test) => isHiddenTest(test))
  // A class exercise's generated checks.
  if (isGeneratedCheckHarness(upgradeLegacyPythonHarness(saved)) && /^\s*checks\s*=/m.test(starterCode)) {
    const prelude = buildSafePrelude(starterCode, ['checks'])
    const hidden = hiddenOf()
    return {
      source: (userCode) => {
        const code = String(userCode)
        const fresh = freshStatePerCheck && !readsInput(code)
        return `${prelude}\n${code}\n${buildSafeCheckHarness(starterCode, hidden, fresh ? code : null)}`
      },
      linesBefore: prelude.split('\n').length,
    }
  }
  const functionName = generatedHarnessFunction(upgradeLegacyPythonHarness(saved))
  if (!functionName) return null
  if (!SETUP_TEST_DATA.every((name) => new RegExp(`^\\s*${name}\\s*=`, 'm').test(starterCode))) return null
  const prelude = buildSafePrelude(starterCode)
  const harness = buildSafeHarness(starterCode, functionName, hiddenOf())
  return { source: (userCode) => `${prelude}\n${userCode}\n${harness}`, linesBefore: prelude.split('\n').length }
}

// A Python traceback from a run counts lines from the start of the source the page assembled
// (setup, then the student's code, then the tests), so the student's line 4 read "line 12"
// (E1, 2026-10-29) - in the terminal and in what the tutor was sent. The numbers that point
// into the student's code (`File "<exec>", line N` and the message's "on line N") become the
// editor's line numbers; any other number is left as it is.
export const studentLineNumbers = (message, linesBefore, codeLineCount) => {
  if (typeof message !== 'string' || !(linesBefore > 0)) return message
  const editorLine = (n) => {
    const line = Number(n) - linesBefore
    return line >= 1 && line <= codeLineCount ? String(line) : n
  }
  return message
    .replace(/(File "<exec>", line )(\d+)/g, (match, head, n) => head + editorLine(n))
    .replace(/(\bon line )(\d+)/g, (match, head, n) => head + editorLine(n))
}

// One C++ test line. A thrown exception fails only this test; the note goes to stdout (the
// student's output) without e.what(), which can quote sizes and indices of hidden inputs.
// The result is a "VIZPI:" line of its own (see parseCppHarness), written only after the
// test has run: in one `cerr << "VIZPI:" << (call)` chain the marker would go out before the
// student's function runs, and anything it prints would land inside the marker line. Keep
// in step with the backend's upgradeLegacyCppHarness (vizpi_backend/services/codeService.js).
export const buildCppTestLine = (call, expected) =>
  `try { bool vizpi_ok = (${call}==${expected}); cerr << "\\nVIZPI:" << vizpi_ok << endl; } catch (...) { cerr << "\\nVIZPI:0" << endl; cout << "A test threw an exception and was marked as failed." << endl; }`

// ---- one run's records (study sessions) ----
//
// In a study session every Run Tests click gets a run id and the hash of the code it ran, and
// both go on every record the run writes (TerminalContext: TERMINAL_RUN / OUTPUT / ERROR,
// TEST_CASE_RESULT and the terminal_output row), so each output, error and test result can
// be tied to the exact code version. The tutor is sent the latest run's (lastRunInfo).

// The code version: fnv1a over the code, written the way Workspace.jsx's getCodeVersion
// writes the code_version it logs, so the two can be compared.
export const codeHash = (code) => {
  const text = String(code ?? '')
  let hash = 2166136261
  for (let i = 0; i < text.length; i += 1) {
    hash ^= text.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return `fnv1a-${(hash >>> 0).toString(16)}`
}

// A new run id: a UUID where the browser offers one (any https page), otherwise the time plus
// randomness, which is unique enough among one student's runs.
export const newRunId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return `run-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

// The exception's name in a Python traceback ("NameError", "SyntaxError", "json.decoder.
// JSONDecodeError"): the first unindented line after the last frame, up to its colon. That is
// the traceback's last line unless the message itself runs over several lines. Null for text
// that is not a Python error, such as "Python engine failed to load.".
export const pythonErrorName = (message) => {
  const lines = String(message ?? '').split(/\r?\n/)
  let start = 0
  lines.forEach((line, index) => {
    if (/^\s+File "/.test(line)) start = index + 1
  })
  // A frame's source line and its ^^^ markers are indented; the exception line is not.
  const line = lines
    .slice(start)
    .find((text) => text.trim() && !/^\s/.test(text) && !text.startsWith('Traceback (most recent call last)'))
  const match = line?.match(/^([A-Za-z_][\w.]*)(?::|$)/)
  return match ? match[1] : null
}

// What the run records call the test at `index`: its testName (every check of a class
// exercise has one), or "Test Case N" as in the generic test_name.
export const testLabel = (testList, index) => {
  const name = (Array.isArray(testList) ? testList : [])[index]?.testName
  return typeof name === 'string' && name.trim() ? name : `Test Case ${index + 1}`
}

// The labels of the tests a run did not pass, in test order.
export const failingTestLabels = (results, testList) =>
  (Array.isArray(results) ? results : []).flatMap((passed, index) => (passed === true ? [] : [testLabel(testList, index)]))
